import { Link, useLocation, useNavigate } from 'react-router-dom';
import { activeMembership, useSession } from '../lib/store';
import { PLATFORM_NAME } from '../lib/brand';

/**
 * One header for every context. What it offers depends on the role the visitor
 * holds with this business: a client never sees admin navigation, and a sitter
 * is never offered the client portal, which RequireRole would turn them away
 * from anyway.
 */
export function SiteHeader() {
  const session = useSession();
  const membership = activeMembership(session);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // The signing page is deliberately chrome-free: someone reading a contract
  // should not be offered navigation out of it.
  if (pathname.startsWith('/sign/')) return null;

  // The platform's own front door is a marketing page with its own header, in
  // its own palette. This one would sit above it in the product's paper
  // styling and read as a second, competing bar.
  if (!session.tenant && pathname === '/') return null;

  const isStaff = membership?.role === 'owner' || membership?.role === 'staff';
  // The portal is a client's own pets and bookings, and RequireRole turns a
  // sitter away from it. Only offer it to the role that can actually open it.
  const isClient = membership?.role === 'client';

  const handleSignOut = () => {
    session.signOut();
    navigate('/');
  };

  return (
    <header className="border-b border-[color:var(--color-rule)] bg-[color:var(--color-sheet)]/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-5 py-3.5">
        <Link to="/" className="flex min-w-0 items-center gap-2.5">
          {/* A logo is rendered, never inlined: an SVG uploaded as a logo is
              untrusted markup, and an <img> cannot run script from it. */}
          {session.tenant?.branding.logoUrl ? (
            <img
              src={session.tenant.branding.logoUrl}
              alt={session.tenant.businessName}
              className="h-8 w-auto max-w-[220px] object-contain"
            />
          ) : (
            <span className="truncate font-display text-lg font-extrabold tracking-tight">
              {/* With no business in context this is the platform's own page,
                  so it carries the platform's own name rather than a second
                  generic one that reads as a different product. */}
              {session.tenant?.businessName ?? PLATFORM_NAME}
            </span>
          )}
        </Link>

        <nav className="flex flex-1 flex-wrap items-center gap-x-5 gap-y-2 text-sm" aria-label="Main">
          {session.tenant && (
            /* The same word means two things either side of the business. To a
               client and a visitor it is the list of what is offered, which is
               the storefront. To the owner it is the rates they charge for it,
               and sending them to their own shop window instead was a dead end.
               Staff cannot edit the catalogue, so for them it stays the shop
               window. */
            <Link
              to={membership?.role === 'owner' ? '/admin/services' : '/'}
              className="hover:text-[color:var(--color-stamp)]"
            >
              Services
            </Link>
          )}
          {isClient && (
            <>
              <Link to="/portal" className="hover:text-[color:var(--color-stamp)]">
                My pets
              </Link>
              <Link to="/portal/videos" className="hover:text-[color:var(--color-stamp)]">
                Videos
              </Link>
            </>
          )}
          {isStaff && (
            <>
              <Link to="/admin" className="hover:text-[color:var(--color-stamp)]">
                Bookings
              </Link>
              <Link to="/admin/schedule" className="hover:text-[color:var(--color-stamp)]">
                Schedule
              </Link>
              <Link to="/admin/clients" className="hover:text-[color:var(--color-stamp)]">
                Clients
              </Link>
              <Link to="/admin/videos" className="hover:text-[color:var(--color-stamp)]">
                Videos
              </Link>
              <Link to="/admin/invoices" className="hover:text-[color:var(--color-stamp)]">
                Invoices
              </Link>
              <Link to="/admin/reports" className="hover:text-[color:var(--color-stamp)]">
                Reports
              </Link>
              {membership?.role === 'owner' && (
                <Link to="/admin/settings" className="hover:text-[color:var(--color-stamp)]">
                  Settings
                </Link>
              )}
            </>
          )}
        </nav>

        {session.user ? (
          <div className="flex items-center gap-3">
            <span className="hidden font-mono text-xs text-[color:var(--color-ink-faint)] sm:inline">
              {session.user.name}
            </span>
            <button type="button" className="btn btn-quiet" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/sign-in" className="btn btn-quiet">
              Sign in
            </Link>
            {session.tenant ? (
              <Link to="/sign-up" className="btn btn-primary">
                Get started
              </Link>
            ) : (
              <Link to="/start-a-business" className="btn btn-primary">
                Get started
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
